import React, { useState, useCallback } from 'react';
import { useRecordContext, useTranslate, useNotify, useRedirect } from 'react-admin';
import { Drawer, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { FuneralShowRedesign } from './FuneralShowRedesign';
import { FuneralShowSidebar } from './FuneralShowSidebar';
import { Funeral } from '../types';
import StartDecButton from './StartDecButton';
import CertificateDropdownButton from './CertificateDropdownButton';
import PhysicalMailButton from './PhysicalMailButton';
import { cloneDefaultLines, urnLinesFor } from 'paperman-contract-defaults';

function MobileActionsMenu() {
  const record = useRecordContext<Funeral>();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCreateContract = () => {
    if (!record) return;
    if (!record.primary_client_id) {
      notify(translate('resources.contracts.primary_client_required'), { type: 'error' });
      handleClose();
      return;
    }
    redirect('/contracts/create', undefined, undefined, undefined, {
      record: {
        funeral_id: record.id,
        client_id: record.primary_client_id,
        lines: [
          ...cloneDefaultLines('at', record.region),
          ...urnLinesFor(record.urn_choice, record.region),
        ],
        is_preneed: false,
        is_monthly: false,
        monthly_payment_amount: null,
      },
    });
    handleClose();
  };

  return (
    <>
      <IconButton onClick={handleClick} size="small" sx={{ padding: '6px', border: '1px solid rgba(0,0,0,0.12)', borderRadius: '6px' }}>
        <MoreVertIcon sx={{ fontSize: 20, color: 'var(--muted-foreground, #6b7280)' }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'actions-button' }}
      >
        <MenuItem onClick={handleClose}>
          {record && <StartDecButton funeralId={record.id} />}
        </MenuItem>
        <MenuItem onClick={() => { handleCreateContract(); }}>
          <AddIcon sx={{ mr: 1, fontSize: 20 }} />
          {translate('resources.contracts.name', { smart_count: 1 })}
        </MenuItem>
        <MenuItem onClick={handleClose}>
          <CertificateDropdownButton />
        </MenuItem>
        <MenuItem onClick={handleClose}>
          <PhysicalMailButton />
        </MenuItem>
        <MenuItem onClick={() => {
          handleClose();
          if (record) redirect('edit', 'funerals', record.id);
        }}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
      </Menu>
    </>
  );
}

export function MobileFuneralShowRedesign() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleMenuClick = useCallback(() => {
    setSidebarOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  return (
    <>
      <FuneralShowRedesign
        onMenuClick={handleMenuClick}
        headerActions={<MobileActionsMenu />}
      />
      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={handleClose}
        PaperProps={{
          sx: { width: '85vw', maxWidth: 360 },
        }}
      >
        <div className="cleo-ui h-full">
          <FuneralShowSidebar />
        </div>
      </Drawer>
    </>
  );
}
